/* 맨 위로 버튼 — 카페24 영문몰(skin148) `.remote_controller` 이식
 *
 * 원본은 카페24 테마가 `body.is-scroll` 을 붙여 주는 구조였다. 여기서는 직접 붙인다.
 *
 * ⚠️ 임계값은 **`scrollTop > 0`** 이다(원본과 같다). "한 화면 내려가면" 같은 값으로
 *    바꾸지 말 것 — 조금만 스크롤해도 나타나는 것이 원본 동작이다.
 * ⚠️ 채널톡 런처를 실측해 `--nt-ch-top` 을 세팅한다. 이 값이 없으면 CSS 폴백(80px)이
 *    쓰이고, 채널톡 여백이 바뀌면 두 버튼이 겹친다.
 */
(() => {
  "use strict";

  const rootElm = document.documentElement;
  const boxElm = document.querySelector("[data-nt-totop]");
  if (!boxElm) return;

  /* ── 스크롤 표시 판정 ─────────────────────────────────────────── */
  let ticking = false;

  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(() => {
      const top = window.pageYOffset || rootElm.scrollTop || 0;
      rootElm.classList.toggle("nt-scrolled", top > 0);
      ticking = false;
    });
  };

  window.addEventListener("scroll", onScroll, { passive: true });
  onScroll();

  /* ── 클릭 ─────────────────────────────────────────────────────── */
  const reduceMotion =
    window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  boxElm.addEventListener("click", (event) => {
    const btn = event.target.closest("[data-nt-totop-btn]");
    if (!btn) return;
    window.scrollTo({ top: 0, behavior: reduceMotion ? "auto" : "smooth" });
  });

  /* ── 채널톡 런처 실측 ─────────────────────────────────────────────
     ⚠️ 런처는 **열린 shadow DOM** 안에 있고 클래스명은 배포마다 바뀐다
        (styled-components 해시). `position: fixed` 인 자식으로 **구조로** 찾는다.
     ⚠️ `#ch-plugin` 자체는 `shadowRoot === null` 이다 — 진짜 host 는
        `#ch-plugin-entry` 의 **첫 자식 div** 다.
     ⚠️ 같은 탐색이 `neoteria-sticky-buy.js` 에도 있다(그쪽은 바를 피해 런처를 밀어
        올리는 `--nt-lift-on` 을 계산한다). **한쪽만 고치면 두 값이 갈린다** —
        런처를 찾는 규칙을 바꿀 때는 두 파일을 함께 볼 것. */
  const chLauncher = () => {
    const entry = document.getElementById("ch-plugin-entry");
    const host = entry && entry.firstElementChild;
    if (!host || !host.shadowRoot) return null;
    const wrap = host.shadowRoot.getElementById("ch-shadow-root-wrapper");
    if (!wrap) return null;
    return (
      Array.prototype.find.call(
        wrap.children,
        (el) => window.getComputedStyle(el).position === "fixed"
      ) || null
    );
  };

  const measureChTop = () => {
    const launcher = chLauncher();
    if (!launcher) return false;
    const cs = window.getComputedStyle(launcher);
    const rect = launcher.getBoundingClientRect();
    const bottom = parseFloat(cs.bottom) || 0;
    const height = rect.height || parseFloat(cs.height) || 0;
    if (height <= 0) return false;
    rootElm.style.setProperty("--nt-ch-top", Math.round(bottom + height) + "px");

    /* ── 가로 정렬 ────────────────────────────────────────────────
       ⚠️ 런처와 이 버튼은 **오른쪽 여백이 다르다**(실측 375: 런처 14px · 버튼 16px).
          위아래로 붙어 있는 두 원이 2px 어긋나 보였다(2026-09-13 제보).
       ⚠️ `right` 를 그대로 베끼지 않는다 — 두 요소의 **폭이 다르면** 여백을 맞춰도
          중심이 어긋난다. 런처의 **중심**(오른쪽 끝에서의 거리)을 넘기고, CSS 가
          자기 폭의 절반을 빼서 중심을 맞춘다.
       ⚠️ 채널톡은 폭·여백을 관리자에서 바꿀 수 있다 — 고정값으로 되돌리지 말 것. */
    const right = parseFloat(cs.right) || 0;
    const width = rect.width || parseFloat(cs.width) || 0;
    if (width > 0) {
      rootElm.style.setProperty("--nt-ch-cx", Math.round(right + width / 2) + "px");
    }
    return true;
  };

  /* 채널톡은 늦게 붙는다. 재시도하고 포기한다 — 못 찾아도 CSS 폴백으로 동작한다.
     ⚠️ 창을 20회(10초) → 60회(30초)로 늘렸다(2026-09-13). 느린 회선에서 10초 안에 못 붙으면
        그 페이지만 폴백 값으로 남아 **다른 페이지와 버튼 위치가 달라 보였다.**
     ⚠️ 첫 시도 간격을 250ms 로 줄였다 — 폴백으로 그렸다가 옮기는 구간이 짧을수록 좋다. */
  let tries = 0;
  const tick = () => {
    if (measureChTop() || ++tries > 60) return;
    window.setTimeout(tick, tries < 8 ? 250 : 500);
  };
  tick();

  /* ⚠️ iOS 인앱은 스크롤 방향에 따라 툴바가 접히면서 런처 위치가 바뀐다.
     `window.resize` 가 항상 발화하지 않으므로 `visualViewport` 도 듣는다
     (카페24에서 값이 한 번만 계산돼 낡은 채로 남던 문제). */
  const remeasure = () => measureChTop();
  window.addEventListener("resize", remeasure);
  window.addEventListener("orientationchange", remeasure);
  if (window.visualViewport) {
    window.visualViewport.addEventListener("resize", remeasure);
    window.visualViewport.addEventListener("scroll", remeasure);
  }
})();
